import { useId, useState } from 'react'
import { CHIP_BASE, CHIP_OFF, CHIP_ON } from '@/components/ui/chip-group'
import { Input } from '@/components/ui/input'
import { companyMark, isCompanySlug } from '@/lib/audition/companies'
import { PRIORITY_COPY, priorityLabel } from '@/lib/audition/job-priorities'
import type {
  FollowUpAnswers,
  FollowUpGroup,
} from '@/lib/audition/priority-followups'
import {
  drawnPile,
  followUpRange,
  isOtherPicked,
  openFollowUpGroups,
  otherOption,
  tapFollowUp,
  typeFollowUp,
} from '@/lib/audition/priority-followups'
import { cn } from '@/lib/utils'
import { CompRangeField } from './comp-range'

/**
 * Phase two of the priority ladder, for one of the three that ended on top.
 *
 * Every rule and every sentence is in `lib/audition/priority-followups.ts`.
 * This file draws what that module says a group is and reports taps back —
 * including which groups are being asked at all, so a gate opening is a
 * longer list here rather than a condition written twice.
 */
export function FollowUpCard({
  priorityId,
  rank,
  answers,
  onChange,
  disabled,
}: {
  priorityId: string
  /** Its place on the ladder, drawn beside the name the way the cards are. */
  rank: number
  answers: FollowUpAnswers
  onChange: (answers: FollowUpAnswers) => void
  disabled?: boolean
}) {
  const uid = useId()
  // Gates are resolved once, here, so the piles below are a plain list. A
  // gated pile is declared after the pile it waits on, so opening one puts
  // the new question directly beneath the chip that just opened it — which
  // is the whole of the announcement a screen reader needs.
  const groups = openFollowUpGroups(priorityId, answers)
  // Drawn first, above the piles, because a priority whose follow-up is a
  // range has the range as its headline rather than as a footnote — and
  // because compensation, the only one that asks a range today, asks nothing
  // else at all.
  const range = followUpRange(priorityId)
  const nameId = `${uid}-name`

  return (
    <section
      aria-labelledby={nameId}
      className="space-y-4 rounded-xl border border-border bg-background p-4 shadow-sm"
    >
      <p id={nameId} className="flex items-baseline gap-2 text-sm">
        <span
          aria-hidden="true"
          className="w-5 shrink-0 font-semibold tabular-nums text-foreground"
        >
          {rank}
        </span>
        <span className="font-semibold text-pretty text-foreground">
          {priorityLabel(priorityId)}
        </span>
      </p>

      {range && (
        <CompRangeField
          question={range}
          answers={answers}
          onChange={onChange}
          disabled={disabled}
        />
      )}

      {groups.map((group) => (
        <Pile
          key={group.id}
          uid={uid}
          group={group}
          answers={answers}
          onChange={onChange}
          disabled={!!disabled}
        />
      ))}
    </section>
  )
}

function Pile({
  uid,
  group,
  answers,
  onChange,
  disabled,
}: {
  uid: string
  group: FollowUpGroup
  answers: FollowUpAnswers
  onChange: (answers: FollowUpAnswers) => void
  disabled: boolean
}) {
  // What has been typed into the search box is INTERACTION, never an answer:
  // it narrows which chips are on screen and nothing more. Stored, it would
  // ride to the admin recap as a half-typed company name nobody chose, and a
  // resumed draft would open with a filter the applicant could not see the
  // reason for.
  const [query, setQuery] = useState('')
  const picked = answers.picks[group.id] ?? []
  // Which companies the search has put on this card, which only ever GROWS
  // while the card is open. Also interaction rather than an answer — it is
  // the set of chips on screen, not the set that is picked.
  //
  // It never shrinks because the alternative is a chip that unmounts under
  // the finger that just un-picked it: focus falls to <body>, and the company
  // they changed their mind about is gone from the pile with no way back to
  // it but typing the name again.
  const [shown, setShown] = useState<string[]>(() =>
    picked.filter((id) => !group.options.some((o) => o.id === id)),
  )
  const chip = otherOption(group)
  const pile = drawnPile(group, shown, query)
  const labelId = `${uid}-${group.id}`
  const hintId = `${uid}-${group.id}-hint`
  const tap = (optionId: string) => {
    if (
      !shown.includes(optionId) &&
      !group.options.some((o) => o.id === optionId)
    )
      setShown((was) => [...was, optionId])
    onChange(tapFollowUp(answers, group, optionId))
  }

  return (
    <div className="space-y-2">
      <p id={labelId} className="text-sm text-muted-foreground text-pretty">
        {group.label}
      </p>
      {group.search && (
        <Input
          type="search"
          // Named for the pile it narrows. "Search" alone appears once per
          // pile that has one, and a screen reader listing the fields on this
          // card would read the same word for each of them.
          aria-label={`${group.label} — ${PRIORITY_COPY.searchLabel}`}
          // The line below the chips is what says whether the typing found
          // anything. Named here so it is read with the box rather than only
          // when the applicant happens to walk past it.
          aria-describedby={hintId}
          placeholder={group.search.placeholder}
          value={query}
          disabled={disabled}
          // This box sits inside the step's <form>, where Enter on a
          // single-line input submits. Typing a company and pressing Enter
          // would leave the step instead of adding the chip — so Enter does
          // what it does in every other filter box, which is nothing. The
          // chips it narrowed to are the next tab stop.
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault()
          }}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      {/* Toggle buttons rather than a radiogroup even where the pile takes
          one answer. Every one of these questions is optional, and a native
          radio has no way back to nothing once a radio is chosen — picking
          one by mistake would be an answer the applicant could not withdraw.
          `tapFollowUp` is what keeps a single-answer pile to one. */}
      <div
        role="group"
        aria-labelledby={labelId}
        className="flex flex-wrap gap-2"
      >
        {pile.options.map((option) => {
          const on = picked.includes(option.id)
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => tap(option.id)}
              className={cn(
                CHIP_BASE,
                on ? CHIP_ON : CHIP_OFF,
                isCompanySlug(option.id) && 'gap-2 pl-1.5',
              )}
            >
              {isCompanySlug(option.id) && (
                // A placeholder for the real mark, which is its own call —
                // and drawn in the page's own neutral rather than in forty
                // competitor brand colours, which would be the loudest thing
                // on a page whose whole accent budget is one teal.
                <span
                  aria-hidden="true"
                  className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
                >
                  {companyMark(option.id)}
                </span>
              )}
              {option.label}
            </button>
          )
        })}
      </div>
      {group.search && (
        // Always mounted, so the live region exists before it has anything to
        // announce — one mounted with its first message is a message nothing
        // reads out. Empty when the chips above already say it themselves.
        <p
          id={hintId}
          aria-live="polite"
          className="text-xs text-muted-foreground"
        >
          {pile.hint}
        </p>
      )}
      {chip && isOtherPicked(group, answers) && (
        <Input
          // Named for the question it belongs to, not merely "Other": the
          // placeholder is a hint that disappears the moment they type, and
          // a box whose only name is its placeholder is a box a screen
          // reader stops being able to identify mid-answer.
          aria-label={`${group.label} — ${PRIORITY_COPY.otherLabel}`}
          placeholder={chip.freeText.placeholder}
          value={answers.typed[group.id] ?? ''}
          disabled={disabled}
          // Same fence as the search box: this one is inside the step's
          // <form> too, and Enter mid-sentence would submit the step rather
          // than finish the thought.
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault()
          }}
          onChange={(e) =>
            onChange(typeFollowUp(answers, group, e.target.value))
          }
        />
      )}
    </div>
  )
}
